import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  CssBaseline,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import UsageIcon from "@mui/icons-material/DataUsage";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { ReactComponent as Logo } from "../assets/guardify-logo.svg"; // Import your SVG logo

const drawerWidth = 240;
const collapsedWidth = 70;

const menuItems = [
  { text: "Usage", icon: <UsageIcon />, path: "/" },
  { text: "Evidence Tools", icon: <AutoAwesomeIcon />, path: "/evidence-tools" },
  { text: "Plan", icon: <WorkspacePremiumIcon />, path: "/plan" },
  { text: "Profile", icon: <PersonIcon />, path: "/profile" },
  { text: "Settings", icon: <SettingsIcon />, path: "/settings" },
];

const Layout = ({ signOut, user, children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();

  // Dropdown Menu State
  const [anchorEl, setAnchorEl] = useState(null);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  return (
    <Box class={isCollapsed ? 'main-box' : 'main-box-sidebar'}  sx={{ display: "flex" }}>
      <CssBaseline />

      {/* App Bar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'linear-gradient(90deg, #191C35 0%, #002966 100%)',
          boxShadow: 'none'
        }}
      >
        <Toolbar>
          {/* Menu Button */}
          <IconButton color="inherit" edge="start" onClick={() => setIsCollapsed(!isCollapsed)} sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>

          {/* SVG Logo */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Logo style={{ height: "26px", width: "auto" }} />
           <div class="evidence-tools-div">| <span>Evidence Tools</span></div>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* User Menu */}
          <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }} onClick={handleMenuOpen}>
            <Avatar sx={{ bgcolor: "#006BBB", width: 32, height: 32, mr: 1 }}>
              {user?.name?.charAt(0) || "U"}
            </Avatar>
            <Typography variant="body1">{user?.name || "User"}</Typography>
          </Box>

          {/* Dropdown Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem component={Link} to="/profile" onClick={handleMenuClose}>
              <PersonIcon sx={{ mr: 1 }} /> Profile
            </MenuItem>
            <MenuItem component={Link} to="/settings" onClick={handleMenuClose}>
              <SettingsIcon sx={{ mr: 1 }} /> Settings
            </MenuItem>
            <MenuItem onClick={signOut}>
              <ExitToAppIcon sx={{ mr: 1 }} /> Sign Out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          width: isCollapsed ? collapsedWidth : drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: isCollapsed ? collapsedWidth : drawerWidth,
            boxSizing: "border-box",
            transition: "width 0.3s",
            marginTop: "64px",
          },
        }}
      >
        <List sx={{ p: 0 }}>
          {menuItems.map((item) => (
            <ListItemButton
              key={item.text}
              component={Link}
              to={item.path}
              selected={location.pathname === item.path}
              sx={{
                display: "flex",
                justifyContent: isCollapsed ? "center" : "flex-start",
                padding: "10px",
                minHeight: "48px",
                "&.Mui-selected": {
                  backgroundColor: "rgba(0, 107, 187, 0.2)",
                  borderLeft: "4px solid #006BBB",
                },
              }}
            >
              {item.icon}
              {!isCollapsed && (
                <ListItemText 
                  primary={item.text} 
                  sx={{ 
                    ml: 2,
                    minWidth: 0,
                    '& .MuiListItemText-primary': {
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }
                  }} 
                />
              )}
            </ListItemButton>
          ))}
        </List>
      </Drawer>

      {/* Main Content */}
      <Box
        className="main-page-div"
        component="main"
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          height: "calc(100vh - 64px)", // Changed from minHeight to height
          marginTop: "64px",
          marginLeft: isCollapsed ? collapsedWidth : drawerWidth, // Use the same width variables
          transition: "margin-left 0.3s",
          overflow: "auto" // Add overflow handling
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
